<template>
  <v-container class="sidebar-container">
    <transition name="fade" mode="out-in">
      <!-- Main Booking Summary -->
      <v-card v-if="page !== 'PaymentInformation'" class="summary-card elevation-2">
        <div class="summary-header">
          <h1 class="summary-title earthwerk-font">BOOKING SUMMARY</h1>
          <h3 class="summary-dates">
            {{ reservation.check_in }}  {{ reservation.check_out }}
            <span class="nights-badge">{{ days }} NIGHT(S)</span>
          </h3>
        </div>

        <div class="summary-sections">
          <!-- Conservation Fees Section -->
          <div class="summary-section">
            <div class="section-header">
              <v-icon class="section-icon">mdi-nature-people</v-icon>
              <h3 class="section-title earthwerk-font">CONSERVATION ENTRY FEES</h3>
            </div>
            <div v-if="allConservationFees.filter(g => g.payment_guests > 0).length === 0" class="empty-state">
              No conservation fees
            </div>
            <div v-else>
              <div v-for="(guest, key) in allConservationFees"
                   :key="key"
                   v-if="guest.payment_guests > 0"
                   class="item-row">
                <div class="item-details">
                  <span class="item-name">{{ guest.payment_person }}</span>
                  <span class="item-quantity"> {{ guest.payment_guests }}</span>
                </div>
                <div class="item-price">
                  {{ browserCurrency }} {{ Math.round(convertConservation(guest)).toLocaleString("en") }}
                </div>
                <v-btn icon small class="delete-btn" @click="removeConservationFee(key)">
                  <v-icon small color="white">mdi-trash-can-outline</v-icon>
                </v-btn>
              </div>
              <div class="section-total">
                <span>Subtotal</span>
                <span class="total-amount">
                  {{ browserCurrency }} {{ Math.round(conservationTotal).toLocaleString("en") }}
                </span>
              </div>
            </div>
          </div>

          <!-- Vehicle Fees Section -->
          <div v-if="$vehicleDisabled === false" class="summary-section">
            <div class="section-header">
              <v-icon class="section-icon">mdi-car</v-icon>
              <h3 class="section-title earthwerk-font">VEHICLE FEES</h3>
            </div>
            <div v-if="allVehicles.filter(v => v.vehicles > 0).length === 0" class="empty-state">
              No vehicle fees
            </div>
            <div v-else>
              <div v-for="(vehicle, key) in allVehicles"
                   :key="key"
                   v-if="vehicle.vehicles > 0"
                   class="item-row">
                <div class="item-details">
                  <span class="item-name">{{ vehicle.vehicle_charge_category }}</span>
                  <span class="item-quantity"> {{ vehicle.vehicles }} vehicles</span>
                </div>
                <div class="item-price">
                  {{ convertVehicle(vehicle) }}
                </div>
                <v-btn icon small class="delete-btn" @click="removeVehicle(key)">
                  <v-icon small color="white">mdi-trash-can-outline</v-icon>
                </v-btn>
              </div>
              <div class="section-total">
                <span>Subtotal</span>
                <span class="total-amount">
                  {{ browserCurrency }} {{ Math.round(vehicleTotal).toLocaleString("en") }}
                </span>
              </div>
            </div>
          </div>

          <!-- Accommodation Section -->
          <div class="summary-section">
            <div class="section-header">
              <v-icon class="section-icon">mdi-bed</v-icon>
              <h3 class="section-title earthwerk-font">ACCOMMODATION</h3>
            </div>
            <div v-if="chosenFacilities.length === 0" class="empty-state">
              No accommodation selected
            </div>
            <div v-else>
              <div v-for="(facility, key) in chosenFacilities"
                   :key="key"
                   class="item-row">
                <div class="item-details">
                  <span class="item-name">{{ facility.name }}</span>
                  <span class="item-quantity"> {{ getDays(facility.start_date, facility.end_date) }} nights</span>
                </div>
                <div class="item-price">
                  {{ browserCurrency }} {{ Math.round(facilityTotal(facility)).toLocaleString("en") }}
                </div>
                <v-btn icon small class="delete-btn" @click="removeFacility(facility)">
                  <v-icon small color="white">mdi-trash-can-outline</v-icon>
                </v-btn>
              </div>
              <div class="section-total">
                <span>Subtotal</span>
                <span class="total-amount">
                  {{ browserCurrency }} {{ Math.round(allFacilityTotal).toLocaleString("en") }}
                </span>
              </div>
            </div>
          </div>

          <!-- Activities Section -->
          <div class="summary-section">
            <div class="section-header">
              <v-icon class="section-icon">mdi-binoculars</v-icon>
              <h3 class="section-title earthwerk-font">CONSERVATION ACTIVITIES</h3>
            </div>
            <div v-if="chosenInventory.length === 0" class="empty-state">
              No activities selected
            </div>
            <div v-else>
              <div v-for="(inventory, index) in chosenInventory"
                   :key="index"
                   class="item-row">
                <div class="item-details">
                  <span class="item-name">{{ inventory.name }}</span>
                  <span class="item-date">{{ formated_date(inventory.date) }}</span>
                  <div v-if="inventory.ranger_marker === false && inventory.adults > 0 && inventory.children > 0"
                       class="item-participants">
                    {{inventory.residency_adult.payment_person}}: {{ inventory.adults }},
                    {{inventory.residency_children.payment_person}}: {{ inventory.children }}
                  </div>
                  <div v-else-if="inventory.ranger_marker === false && inventory.adults > 0"
                       class="item-participants">
                    {{inventory.residency_adult.payment_person}}: {{ inventory.adults }}
                  </div>
                  <div v-else-if="inventory.ranger_marker === false && inventory.children > 0"
                       class="item-participants">
                    {{inventory.residency_children.payment_person}}: {{ inventory.children }}
                  </div>
                  <div v-else-if="inventory.ranger_marker === true"
                       class="item-participants">
                    Rangers: {{ inventory.adults }}
                  </div>
                </div>
                <div class="item-price">
                  {{ browserCurrency }} {{ Math.round(findActivityTotal(inventory)).toLocaleString("en") }}
                </div>
                <v-btn icon small class="delete-btn" @click="removeInventory(index)">
                  <v-icon small color="white">mdi-trash-can-outline</v-icon>
                </v-btn>
              </div>
              <div class="section-total">
                <span>Subtotal</span>
                <span class="total-amount">
                  {{ browserCurrency }} {{ Math.round(allInventoryTotal).toLocaleString("en") }}
                </span>
              </div>
            </div>
          </div>

          <!-- Donations Section -->
          <div class="summary-section">
            <div class="section-header">
              <v-icon class="section-icon">mdi-heart</v-icon>
              <h3 class="section-title earthwerk-font">DONATIONS</h3>
            </div>
            <div v-if="allPublicDonations.length <= 0" class="empty-state">
              No donations
            </div>
            <div v-else>
              <div v-for="(donation, index) in allPublicDonations"
                   :key="index"
                   class="item-row">
                <div class="item-details">
                  <span class="item-name">{{ donation.cause }}</span>
                </div>
                <div class="item-price">
                  {{ donation.currency_name }} {{ donation.amount }}
                </div>
                <v-btn icon small class="delete-btn" @click="removeDonation(index)">
                  <v-icon small color="white">mdi-trash-can-outline</v-icon>
                </v-btn>
              </div>
            </div>
          </div>

          <!-- Proof of Residency Section -->
          <div v-if="proofOfResidency.length > 0" class="summary-section">
            <div class="section-header">
              <v-icon class="section-icon">mdi-account-card-details</v-icon>
              <h3 class="section-title earthwerk-font">PROOF OF RESIDENCY</h3>
            </div>
            <div v-for="(proof, index) in proofOfResidency" :key="index" class="residency-item">
              <div v-if="proof.first_name != '' && proof.last_name!='' && proof.document_id!=''"
                   class="residency-details">
                <div class="residency-name">{{ proof.first_name }} {{ proof.last_name }}</div>
                <div class="residency-id">{{ proof.payment_person }} {{ proof.document_id }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Grand Total -->
        <div class="grand-total">
          <v-divider class="total-divider"></v-divider>
          <div class="total-amount">
            <span>TOTAL AMOUNT</span>
            <span class="amount">{{ browserCurrency }} {{ Math.round(totalPrice).toLocaleString("en") }}</span>
          </div>
        </div>
      </v-card>

      <!-- Payment Information View -->
      <v-card v-else class="payment-card elevation-2">
        <div class="payment-header">
          <h2 class="payment-title earthwerk-font">BOOKING TOTAL</h2>
        </div>

        <div v-if="paymentUSDPrice === undefined" class="loading-container">
          <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        </div>

        <div v-else class="payment-details">
          <div class="payment-amount">
            <span class="currency">{{ browserCurrency }}</span>
            <span class="amount">{{ $roundOff(paymentUSDPrice) }}</span>
          </div>

          <div v-if="allOnlinepaymentDonations.length > 0" class="donation-section">
            <h4 class="donation-title earthwerk-font">CONSERVATION DONATION</h4>
            <div v-for="(donation, index) in allOnlinepaymentDonations"
                 :key="index"
                 class="donation-item">
              <span class="donation-amount">
                {{ donation.new_donation_currency_name }} {{ donation.new_donation_amount }}
              </span>
              <v-btn icon small
                     class="delete-btn donation"
                     @click="removeOnlineDonation(index)">
                <v-icon small color="white">mdi-trash-can-outline</v-icon>
              </v-btn>
            </div>
          </div>
        </div>
      </v-card>
    </transition>
  </v-container>
</template>

<script>
  import Guests from "./Guests";

  export default {
    name: "BookingSummary",
    components: {
      Guests,
    },
    data() {
      return {
        route: "",
        page: this.$route.name,
        total: 0,
        route: this.$route.name,
        date_settings: {
          night: "Night",
          nights: "Nights",
          "day-names": ["Sun", "Mon", "Tue", "Wed", "Thur", "Fri", "Sat"],
          "check-in": "Check-in",
          "check-out": "Check-Out",
          "month-names": [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
          ],
        },
        reservation: {
          days: 0,
          currency: localStorage.getItem("currency"),
        },
        booking: false,
        donations: localStorage.getItem("donation"),
      };
    },
    computed: {
      days: function () {
        return Number(
          this.getDays(
            JSON.parse(localStorage.getItem("user_data")).check_in,
            JSON.parse(localStorage.getItem("user_data")).check_out
          )
        );
      },
      totalPrice: function () {
        var donationAmout = 0;
        if (this.allPublicDonations.length > 0) {
          if (this.allPublicDonations[0].currency === this.browserCurrencyID) {
            donationAmout = Number(this.allPublicDonations[0].amount);
          } else {
            if (this.browserCurrencyID === "6bf74e49-7ff6-4555-a080-15541d62204a") {
              donationAmout = Number(this.allPublicDonations[0].amount);
            } else {
              donationAmout = Number(
                this.convertBasedBookingCreationDate(
                  this.allPublicDonations[0].currency_name,
                  Number(this.allPublicDonations[0].amount),
                  this.allPublicDonations[0].currency_buy_rate,
                  this.allPublicDonations[0].currency_sell_rate
                ).amount
              );
            }
          }
        }

        var activity = 0;
        for (var i = 0; i < this.chosenInventory.length; i++) {
          activity = activity + this.findActivityTotal(this.chosenInventory[i]);
        }

        var facility = 0;
        for (var j = 0; j < this.chosenFacilities.length; j++) {
          facility = facility + Number(this.facilityTotal(this.chosenFacilities[j]));
        }

        var vehicleData = this.vehicleTotal;
        var conservationData = this.conservationTotal;

        return activity + facility + vehicleData + conservationData + donationAmout;
      },
      paymentKESPrice: function () {
        if (this.singleBooking.currency !== undefined) {
          var total = 0;
          total = this.convert2(
            this.singleBooking.currency,
            "KES",
            this.singleBooking.booking_payment_balance
          ).amount;
          return total;
        }
      },
      paymentUSDPrice: function () {
        let donationAmout = this.totalOnlineDonations
        if (this.singleBooking.currency !== undefined) {
          var total = 0;
          total = Number(
            this.convertBasedBookingCreationDate(
              this.singleBooking.currency,
              this.singleBooking.booking_payment_balance,
              this.singleBooking.currency_buying_rate_at_time,
              this.singleBooking.currency_selling_rate_at_time
            ).amount
          );
          total = total + Math.ceil(donationAmout);
          return total;
        }
      },
    },
    created() {
      if (this.$route.name !== "PaymentInformation") {
        this.route = this.$route.name;
        this.$store.commit(
          "BOOKING_FACILITIES",
          JSON.parse(localStorage.getItem("facility_data"))
        );
        this.$store.commit(
          "CHOSEN_ACTIVITIES",
          JSON.parse(localStorage.getItem("activity_data"))
        );

        if (localStorage.getItem("conservation_data") !== null) {
          this.$store.commit(
            "LOCAL_CONSERVATION",
            JSON.parse(localStorage.getItem("conservation_data"))
          );
        }
        if (localStorage.getItem("vehicle_data") !== null) {
          this.$store.commit(
            "LOCAL_VEHICLES",
            JSON.parse(localStorage.getItem("vehicle_data"))
          );
        }
      }
    },
    methods: {
      convertVehicle: function (data) {
        var total = 0;
        total =
          this.convert1(
            data.vehicle_charge_cost_currency,
            data.vehicle_charge_category_cost
          ).amount * Number(data.vehicles);
        return (
          this.$store.state.CurrencyStore.browser_currency +
          " " +
          Number(total * this.days).toLocaleString("en")
        );
      },
      checkInDate: function (data) {
        var date = data;
        date = this.$moment(date).format("YYYY-MM-DD");
        this.reservation.check_in = date;
      },
      checkOutDate: function (data) {
        var date = data;
        date = this.$moment(date).format("YYYY-MM-DD");
        this.reservation.check_out = date;
        this.adjustDates(this.reservation.check_in, this.reservation.check_out);
      },
      selectAccomodation: function () {
        var selectedAccomodation = {
          public_id: this.singleFacility.public_id,
          facility_type: this.singleFacility.facility_type_id,
          accommodation_type: this.singleFacility.accomodation_type_id,
          analysis_code: this.singleFacility.analysis_code,
          start_date: JSON.parse(localStorage.getItem("user_data")).check_in,
          end_date: JSON.parse(localStorage.getItem("user_data")).check_out,
          name: this.singleFacility.name,
          adult_price: this.singleFacility.price.adult_price,
          child_price: this.singleFacility.price.child_price,
          mandatory_price: this.singleFacility.price.mandatory_price,
          children: 0,
          adults: this.getCoservationGuests(),
          currency: this.singleFacility.price.currency_type,
        };
        localStorage.setItem(
          "facility_data",
          JSON.stringify([selectedAccomodation])
        );
        this.$store.commit("BROWSER_FACILITIES", [selectedAccomodation]);
        this.$router.push({
          name: "ActivitySelection",
        });
      },
    },
  };
</script>

<style scoped>
  @font-face {
    font-family: 'Earthwerk';
    src: url('PizzaDude.dk-Earthwerk.otf');
    font-weight: normal;
    font-style: normal;
  }

  .sidebar-container {
    padding: 0;
    max-width: 100%;
    height: 100%;
  }

  /* Card Styles */
  .summary-card, .payment-card {
    border-radius: 12px !important;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08) !important;
    overflow: hidden;
    background: white;
    transition: all 0.3s ease;
  }

    .summary-card:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15) !important;
    }

  /* Header Styles */
  .summary-header, .payment-header {
    padding: 24px;
    background: linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%);
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  }

  .summary-title, .payment-title {
    font-family: 'Earthwerk', sans-serif;
    font-size: 24px;
    font-weight: 600;
    color: #2d3748;
    margin: 0;
    text-align: center;
    letter-spacing: 1px;
    text-transform: uppercase;
  }

  .summary-dates {
    font-size: 16px;
    font-weight: 500;
    color: #4a5568;
    margin: 8px 0 0;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .nights-badge {
    background: rgba(116, 165, 51, 0.9);
    color: white;
    padding: 4px 12px;
    border-radius: 16px;
    font-size: 12px;
    font-weight: 600;
    margin-left: 8px;
    text-transform: uppercase;
  }

  /* Section Styles */
  .summary-sections {
    padding: 0 24px;
    max-height: calc(100vh - 300px);
    overflow-y: auto;
  }

  .summary-section {
    padding: 20px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  }

    .summary-section:last-child {
      border-bottom: none;
    }

  .section-header {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
  }

  .section-icon {
    margin-right: 12px;
    color: #74a533;
  }

  .section-title {
    font-family: 'Earthwerk', sans-serif;
    font-size: 16px;
    font-weight: 600;
    color: #2d3748;
    margin: 0;
    letter-spacing: 1px;
    text-transform: uppercase;
  }

  .empty-state {
    color: #a0aec0;
    font-size: 14px;
    text-align: center;
    padding: 12px 0;
    font-style: italic;
  }

  /* Item Row Styles */
  .item-row {
    display: flex;
    align-items: center;
    padding: 12px 0;
    position: relative;
    transition: all 0.2s ease;
  }

    .item-row:hover {
      background-color: rgba(245, 247, 250, 0.5);
    }

  .item-details {
    flex: 1;
    min-width: 0;
  }

  .item-name {
    font-weight: 500;
    color: #2d3748;
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .item-quantity, .item-date, .item-participants {
    font-size: 12px;
    color: #718096;
    margin-top: 2px;
  }

  .item-price {
    font-weight: 600;
    color: #2d3748;
    margin-left: 16px;
    white-space: nowrap;
  }

  /* Delete Button Styles */
  .delete-btn {
    margin-left: 12px;
    background: #ff4444 !important;
    color: white !important;
    border-radius: 50%;
    width: 28px !important;
    height: 28px !important;
    box-shadow: 0 2px 6px rgba(204, 0, 0, 0.3);
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    opacity: 1 !important;
    visibility: visible !important;
    display: flex !important;
    align-items: center;
    justify-content: center;
  }

    .delete-btn:hover {
      transform: scale(1.1);
      background: #ff3333 !important;
    }

    .delete-btn:active {
      transform: scale(0.95);
    }

    .delete-btn.donation {
      background: #ff6666 !important;
    }

  /* Section Total */
  .section-total {
    display: flex;
    justify-content: space-between;
    padding: 12px 0;
    font-weight: 500;
    color: #4a5568;
    border-top: 1px dashed rgba(0, 0, 0, 0.1);
    margin-top: 8px;
  }

  .total-amount {
    color: #2d3748;
    font-weight: 600;
  }

  /* Residency Item Styles */
  .residency-item {
    padding: 12px 0;
  }

  .residency-details {
    background: #f8fafc;
    border-radius: 8px;
    padding: 12px;
    border: 1px solid rgba(0, 0, 0, 0.05);
  }

  .residency-name {
    font-weight: 500;
    color: #2d3748;
  }

  .residency-id {
    font-size: 12px;
    color: #718096;
    margin-top: 4px;
  }

  /* Grand Total Styles */
  .grand-total {
    padding: 0 24px 24px;
    background-color: #f8fafc;
    margin-top: 16px;
    border-radius: 0 0 12px 12px;
  }

  .total-divider {
    margin: 16px 0;
  }

  .total-amount {
    display: flex;
    justify-content: space-between;
    font-size: 18px;
    font-weight: 600;
    color: #2d3748;
    text-transform: uppercase;
  }

    .total-amount .amount {
      color: #74a533;
    }

  /* Payment Card Styles */
  .payment-header {
    padding: 24px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  }

  .loading-container {
    display: flex;
    justify-content: center;
    padding: 40px;
  }

  .payment-details {
    padding: 24px;
  }

  .payment-amount {
    display: flex;
    align-items: baseline;
    justify-content: center;
    margin-bottom: 24px;
  }

    .payment-amount .currency {
      font-size: 24px;
      font-weight: 500;
      color: #4a5568;
      margin-right: 8px;
    }

    .payment-amount .amount {
      font-size: 36px;
      font-weight: 700;
      color: #74a533;
    }

  .donation-section {
    padding-top: 24px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  }

  .donation-title {
    font-family: 'Earthwerk', sans-serif;
    font-size: 16px;
    font-weight: 500;
    color: #4a5568;
    text-align: center;
    margin: 0 0 16px;
    letter-spacing: 1px;
    text-transform: uppercase;
  }

  .donation-item {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 8px;
  }

  .donation-amount {
    font-weight: 500;
    color: #2d3748;
  }

  /* Transitions */
  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s ease;
  }

  .fade-enter, .fade-leave-to {
    opacity: 0;
  }

  /* Responsive Adjustments */
  @media (max-width: 960px) {
    .summary-title, .payment-title {
      font-size: 20px;
    }

    .summary-dates {
      font-size: 14px;
    }

    .section-title {
      font-size: 14px;
    }
  }

  @media (max-width: 600px) {
    .summary-header, .payment-header {
      padding: 16px;
    }

    .summary-sections, .payment-details {
      padding: 0 16px;
    }

    .grand-total {
      padding: 0 16px 16px;
    }

    .payment-amount .amount {
      font-size: 28px;
    }

    .summary-title, .payment-title {
      font-size: 18px;
    }

    .section-title {
      font-size: 13px;
    }

    .item-name {
      font-size: 14px;
    }

    .item-price {
      font-size: 14px;
    }

    .delete-btn {
      width: 24px !important;
      height: 24px !important;
    }
  }

  @media (max-width: 400px) {
    .summary-header, .payment-header {
      padding: 12px;
    }

    .summary-title, .payment-title {
      font-size: 16px;
    }

    .section-icon {
      margin-right: 8px;
      font-size: 18px;
    }

    .item-row {
      padding: 8px 0;
    }

    .delete-btn {
      margin-left: 8px;
    }
  }
</style>
